import { callViaHTTP, submitInterWorkerMessage } from './api.js'
import {
  ERROR_CODES,
  LOCAL_CALL_TARGET,
  MESSAGE_TYPES,
  TIMING_CONSTANTS,
  TRANSPORT_MODES,
  type TransportMode,
} from './constants.js'
import { packInterWorkerMessage, unpackInterWorkerMessageData } from './inter_worker_message.js'
import type {
  CallOptions,
  FlatSandbox,
  RPCContext,
  RequestMessageData,
  ResponseMessageData,
  WorkerRequest,
} from './types.js'
import { bytesToHex } from './utils.js'
import WaitMap from './wait_map.js'
import { unpackWorkerRequestPayload, validateWorkerRequest } from './worker_request.js'

const randomUUID = (): string => globalThis.crypto.randomUUID()

export interface ExecutorOptions {
  sandbox: FlatSandbox
  serviceUrl: string
  privKey: Uint8Array
  publicKey: Uint8Array
  callTimeout: number
  transport?: TransportMode
  wsConnected?: () => boolean
}

export interface Executor {
  call(
    receiver_public_key: string,
    method: string,
    params?: unknown[],
    opts?: CallOptions,
  ): Promise<unknown>
  callSandboxMethod(ctx: RPCContext, method: string, params: unknown[]): unknown | Promise<unknown>
  processInterWorkerMessage(request: WorkerRequest): Promise<void>
  processInnerWorkerMessage(request: WorkerRequest): Promise<void>
}

export default ({
  sandbox,
  serviceUrl,
  privKey,
  publicKey,
  callTimeout,
  transport = TRANSPORT_MODES.AUTO,
  wsConnected = () => false,
}: ExecutorOptions): Executor => {
  const waitMap = WaitMap()

  const validateCallParams = (
    receiverPublicKey: string,
    method: string,
    params: unknown[],
  ): void => {
    if (!receiverPublicKey) throw new Error('receiver_public_key is required')
    if (!method) throw new Error('method is required')
    if (!Array.isArray(params)) throw new Error(`params must be an array, got: ${typeof params}`)
  }

  const selfPublicKeyHex = bytesToHex(publicKey).toLowerCase()

  const call = async (
    receiver_public_key: string,
    method: string,
    params: unknown[] = [],
    opts: CallOptions = {},
  ): Promise<unknown> => {
    validateCallParams(receiver_public_key, method, params)

    if (
      receiver_public_key === LOCAL_CALL_TARGET ||
      receiver_public_key.toLowerCase() === selfPublicKeyHex
    ) {
      const ctx: RPCContext = {
        request: { public_key: selfPublicKeyHex },
        headers: opts.headers || {},
      }
      return callSandboxMethod(ctx, method, params)
    }

    let selectedTransport: TransportMode = opts.transport || transport

    if (selectedTransport === TRANSPORT_MODES.AUTO) {
      selectedTransport = wsConnected() ? TRANSPORT_MODES.WS : TRANSPORT_MODES.HTTP
    }

    try {
      if (selectedTransport === TRANSPORT_MODES.HTTP) {
        return await callViaHTTPTransport(receiver_public_key, method, params, opts)
      }
      return await callViaWSTransport(receiver_public_key, method, params, opts)
    } catch (err: any) {
      err.receiverPublicKey = receiver_public_key
      err.method = method
      if (!err.message.includes(receiver_public_key)) {
        err.message = `call ${receiver_public_key}::${method}: ${err.message}`
      }
      throw err
    }
  }

  const callViaWSTransport = async (
    receiver_public_key: string,
    method: string,
    params: unknown[],
    opts: CallOptions,
  ): Promise<unknown> => {
    if (!wsConnected()) {
      throw new Error('WebSocket not connected, use transport: "http" or enable WebSocket')
    }

    const request_id = randomUUID()
    const expireAt = new Date(Date.now() + TIMING_CONSTANTS.MESSAGE_EXPIRE_TIME).toISOString()

    const encryptedMessage = await packInterWorkerMessage(privKey, {
      publicKey,
      receiver_public_key,
      data: {
        type: MESSAGE_TYPES.REQUEST,
        request_id,
        method,
        params,
        headers: opts.headers || {},
      },
      expire_at: expireAt,
    })

    await submitInterWorkerMessage({
      publicKey,
      privKey,
      serviceUrl,
      payload: encryptedMessage,
    })

    return new Promise((resolve, reject) => {
      waitMap.addRequest({
        request_id,
        resolve,
        reject,
        timeout: opts.callTimeout || callTimeout,
        locator: `${receiver_public_key}::${method}`,
      })
    })
  }

  const callViaHTTPTransport = async (
    receiver_public_key: string,
    method: string,
    params: unknown[],
    opts: CallOptions,
  ): Promise<unknown> => {
    return callViaHTTP({
      publicKey,
      privKey,
      serviceUrl,
      receiver: receiver_public_key,
      method,
      params,
      headers: opts.headers || {},
      timeout: opts.callTimeout || callTimeout,
    })
  }

  const processInterWorkerMessage = async (request: WorkerRequest): Promise<void> => {
    validateWorkerRequest(request)

    const message = unpackWorkerRequestPayload(request) as {
      sender_public_key: string
      data: string
      salt: string
    }

    const data = await unpackInterWorkerMessageData(privKey, message)

    switch (data.type) {
      case MESSAGE_TYPES.REQUEST:
        return processRequestMessage(request, data)

      case MESSAGE_TYPES.RESPONSE:
        return processResponseMessage(request, data)

      default:
        throw new Error(`unknown message type: ${(data as any).type}`)
    }
  }

  const processRequestMessage = async (
    request: WorkerRequest,
    { request_id, method, params, headers = {} }: RequestMessageData,
  ): Promise<void> => {
    let responseData: unknown = null
    let error: any = null

    try {
      responseData = await callSandboxMethod({ request, headers }, method, params)
    } catch (e) {
      error = e
    }

    let code: number = ERROR_CODES.SUCCESS
    if (error) {
      code = (error as { code?: number }).code || ERROR_CODES.INTERNAL_ERROR
    }

    const expireAt = new Date(Date.now() + TIMING_CONSTANTS.MESSAGE_EXPIRE_TIME).toISOString()

    const encryptedResponse = await packInterWorkerMessage(privKey, {
      publicKey,
      receiver_public_key: request.public_key,
      data: {
        type: MESSAGE_TYPES.RESPONSE,
        request_id,
        code,
        message: error ? error.toString() : '',
        data: responseData,
      },
      expire_at: expireAt,
    })

    const responsePayload = {
      ...encryptedResponse,
      request_id,
    }

    await submitInterWorkerMessage({
      publicKey,
      privKey,
      serviceUrl,
      payload: responsePayload,
    })
  }

  const processResponseMessage = async (
    _request: WorkerRequest,
    { request_id, code, message, data }: ResponseMessageData,
  ): Promise<void> => {
    if (code !== ERROR_CODES.SUCCESS) {
      waitMap.rejectRequest(request_id, code, message)
      return
    }

    waitMap.resolveRequest(request_id, data)
  }

  const callSandboxMethod = (
    ctx: RPCContext,
    method: string,
    params: unknown[],
  ): unknown | Promise<unknown> => {
    const fn = sandbox[method]

    if (!fn) {
      const error = new Error(`method not found: ${method}`) as Error & { code?: number }
      error.code = ERROR_CODES.NOT_FOUND
      throw error
    }

    return fn(ctx, ...params)
  }

  return {
    call,
    callSandboxMethod,
    processInterWorkerMessage,
    processInnerWorkerMessage: processInterWorkerMessage,
  }
}
